import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Download } from "lucide-react";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-surface px-5 py-4 text-base text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-cyan-300/50";
const labelCls = "mb-2 block text-left text-base font-medium text-white/70";

const SubscribeSection = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", city: "" });
  const [status, setStatus] = useState("idle");
  const [memberId, setMemberId] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/leads/subscribers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setMemberId(data.membership_id || null);
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="subscribe" data-testid="subscribe-section" className="relative overflow-hidden border-t border-white/5 py-28 md:py-40">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[480px] w-[820px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(139,92,246,0.10),transparent)]" />
      <div className="relative mx-auto max-w-3xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <p className="text-gradient mb-6 font-mono text-base uppercase tracking-[0.3em]">
            {t("sub.eyebrow", "Stay Close to the Research")}
          </p>
          <h2 className="mx-auto max-w-2xl font-display text-[2rem] font-semibold leading-[1.08] tracking-tight sm:text-[2.75rem]">
            {t("sub.h2a", "Subscribe for updates.")} <span className="text-gradient">{t("sub.h2b", "Join the community.")}</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg font-light leading-relaxed text-fog">
            {t("sub.p", "Research news, publications and ecosystem updates, direct to your inbox. Every new subscriber is welcomed with a Digital Membership Card as a Prime Member, with the first month free.")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 rounded-3xl bg-gradient-to-br from-violet-500/40 via-blue-500/15 to-cyan-400/40 p-[1px]"
        >
          <div className="rounded-3xl bg-surface p-8 md:p-12">
            {status === "done" ? (
              <div data-testid="subscribe-success" className="py-6 text-center">
                <CheckCircle2 className="mx-auto h-12 w-12 text-cyan-300" />
                <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight">
                  {t("sub.success.h", "Welcome to the community.")}
                </h3>
                <p className="mx-auto mt-4 max-w-md text-lg font-light leading-relaxed text-fog">
                  {t("sub.success.p", "Your Digital Membership Card as a Prime Member is below, and a copy is on its way to your inbox, with your first month free.")}
                </p>
                {memberId && (
                  <div className="mt-10">
                    <img
                      src={`${API}/membership/card/${memberId}`}
                      alt="Your Digital Membership Card"
                      data-testid="subscribe-membership-card"
                      className="mx-auto w-full max-w-xl rounded-2xl border border-white/10 shadow-[0_16px_60px_rgba(59,130,246,0.18)]"
                    />
                    <p data-testid="subscribe-membership-id" className="text-gradient mt-5 font-mono text-base font-semibold tracking-[0.2em]">
                      {memberId}
                    </p>
                    <a
                      href={`${API}/membership/card/${memberId}/download`}
                      data-testid="subscribe-card-download"
                      className="bg-gradient-accent mt-6 inline-flex items-center gap-3 rounded-full px-8 py-3.5 text-base font-semibold text-white shadow-[0_8px_32px_rgba(59,130,246,0.35)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(34,211,238,0.5)]"
                    >
                      <Download className="h-4 w-4" />
                      {t("sub.download", "Download Membership Card")}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <form data-testid="subscribe-form" onSubmit={submit} className="grid gap-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label className={labelCls} htmlFor="sub-name">{t("form.name", "Full name")}</label>
                    <input id="sub-name" data-testid="subscribe-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder={t("form.ph.name", "Your name")} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="sub-email">{t("form.email", "Email")}</label>
                    <input id="sub-email" data-testid="subscribe-email-input" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  </div>
                </div>
                <div className="grid gap-6 md:grid-cols-3">
                  <div>
                    <label className={labelCls} htmlFor="sub-phone">{t("form.phone", "Contact number")}</label>
                    <input id="sub-phone" data-testid="subscribe-phone-input" required className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="sub-country">{t("form.country", "Country")}</label>
                    <input id="sub-country" data-testid="subscribe-country-input" required className={inputCls} value={form.country} onChange={set("country")} placeholder={t("form.ph.country", "India")} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="sub-city">{t("form.city", "City")}</label>
                    <input id="sub-city" data-testid="subscribe-city-input" required className={inputCls} value={form.city} onChange={set("city")} placeholder={t("form.ph.city", "Mumbai")} />
                  </div>
                </div>
                {status === "error" && (
                  <p data-testid="subscribe-error" className="text-base text-rose-300">
                    {t("form.error", "Something went wrong. Please try again or email invest@thelifespanproject.com.")}
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="subscribe-submit-button"
                  disabled={status === "sending"}
                  className="group mx-auto flex items-center gap-3 rounded-full bg-gradient-accent px-6 py-3.5 text-base font-semibold text-white shadow-[0_8px_32px_rgba(59,130,246,0.35)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(34,211,238,0.5)] disabled:opacity-60 sm:px-10"
                >
                  {status === "sending" ? t("sub.sending", "Subscribing...") : t("sub.submit", "Subscribe for Updates")}
                  <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SubscribeSection;
